import { useMutation, useQuery } from '@apollo/client/react/hooks';
import AddHollow from '@clearkit/icons/glyphs/AddHollow';
import CircularProgress from '@material-ui/core/CircularProgress';
import { CKBox, CKButton, CKTooltip } from 'clearkit';
import { useEffect, useMemo } from 'react';
import { Prompt } from 'react-router-dom';

import FormSection from '~/components/configuration/FormSection';
import ToastContainer from '~/containers/Toasts';
import useNavigationEditGuard from '~/hooks/useNavigationEditGuard';
import { configurationNameForType } from '~/lib/configuration';

import attributesQuery from './attributes.graphql';
import fieldMappingsQuery from './fieldMappings.graphql';
import FormFieldMappingRow from './FormFieldMappingRow';
import upsertFieldMappingsMutation from './upsertFieldMappings.graphql';

const STUB_MAPPING = {
  id: null,
  sourceAttribute: null,
  targetAttribute: null,
};

const INTEGRATION_ENRICHMENT_TYPE_MAPPING = {
  hubspot: 'hubspot_contact',
  pardot: 'pardot_prospect',
  marketo: 'marketo_form_lead',
};

export default function FormFieldMappings({ integration }) {
  const configurationName = configurationNameForType(integration);
  const [fieldMappings, setFieldMappings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mappingsAreDirty, setMappingsAreDirty] = useState(false);
  const [showInvalidFields, setShowInvalidFields] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});

  useNavigationEditGuard(() => mappingsAreDirty);

  const handleMappingChange = (index) => (changes) => {
    const newFieldMappings = [...fieldMappings];
    newFieldMappings[index] = {
      ...newFieldMappings[index],
      ...changes,
    };
    setFieldMappings(newFieldMappings);
    setMappingsAreDirty(true);
  };

  const addMapping = () => {
    setFieldMappings([...fieldMappings, STUB_MAPPING]);
    setMappingsAreDirty(true);
  };

  const removeMapping = (indexToRemove) => () => {
    const isPersisted = Boolean(fieldMappings[indexToRemove].id);

    if (!isPersisted) {
      return filterMappings(indexToRemove);
    }

    if (getUserConfirmation()) {
      setMappingsAreDirty(true);
      return filterMappings(indexToRemove);
    }
  };

  const getUserConfirmation = () =>
    window.confirm('Are you sure you want to remove this field mapping ?');
  const filterMappings = (indexToRemove) =>
    setFieldMappings(fieldMappings.filter((_, i) => i !== indexToRemove));

  const {
    data: fieldMappingData = {},
    loading: mappingsLoading,
    refetch: refetchFieldMappings,
  } = useQuery(fieldMappingsQuery, {
    displayName: 'fieldMappingsQuery',
    fetchPolicy: 'no-cache',
    variables: {
      integration,
    },
    notifyOnNetworkStatusChange: true,
  });

  const targetEnrichmentAttributesType =
    INTEGRATION_ENRICHMENT_TYPE_MAPPING[integration];

  const {
    data: attributesData = {},
    loading: attributesLoading,
    refetch,
  } = useQuery(attributesQuery, {
    displayName: 'attributesQuery',
    fetchPolicy: 'no-cache',
    variables: {
      refreshAttributes: false,
      targetTypes: [targetEnrichmentAttributesType],
    },
  });

  const onClickRefreshAttributes = () => {
    return refetch({ refreshAttributes: true });
  };

  const [updateFieldMapping, { loading: saveMappingsLoading }] = useMutation(
    upsertFieldMappingsMutation,
  );

  const saveFieldMappings = async () => {
    try {
      await updateFieldMapping({
        variables: {
          input: {
            integration: integration,
            fieldMappings: fieldMappings,
          },
        },
      });
      setMappingsAreDirty(false);

      ToastContainer.addSuccess(
        `${configurationName} Form Field Mappings Updated`,
      );

      return refetchFieldMappings();
    } catch (e) {
      ToastContainer.addError('There was a problem saving your field mappings');
    }
  };

  const savedFieldMappings = fieldMappingData?.formFieldMapping?.fieldMappings;

  const updateErrors = (key, value) => {
    const errors = fieldErrors;
    if (value === undefined) {
      delete errors[key];
      setFieldErrors(errors);
    } else if (errors[key] === undefined) {
      errors[key] = [value];
      setFieldErrors(errors);
    } else {
      errors[key].push(value);
      setFieldErrors(errors);
    }
  };

  const findDuplicates = (array) => {
    return array.filter((value, index, arry) => {
      if (arry.indexOf(value) !== index) return value;
    }, []);
  };

  const areFieldMappingsInvalid = useMemo(() => {
    const nullFields = fieldMappings
      .map((fm) => [fm.targetAttribute, fm.sourceAttribute])
      .flat()
      .includes(null);

    const targetMappings = fieldMappings.map((fm) => fm.targetAttribute);
    const targetFieldDuplicates = findDuplicates(targetMappings);
    if (targetFieldDuplicates.length > 0) {
      targetFieldDuplicates.forEach((targetField) => {
        updateErrors(targetField);
        updateErrors(targetField, 'duplicate field');
      });
    } else {
      setFieldErrors({});
    }

    return nullFields || targetFieldDuplicates.length > 0;
  }, [fieldMappings]);

  useEffect(() => {
    if (savedFieldMappings?.length > 0) {
      setFieldMappings([...savedFieldMappings]);
    }
  }, [savedFieldMappings]);

  useEffect(() => {
    setLoading(attributesLoading || mappingsLoading);
  }, [attributesLoading, mappingsLoading]);

  const SaveButton = () => {
    return (
      <CKButton
        isDisabled={areFieldMappingsInvalid}
        isLoading={saveMappingsLoading || loading}
        isSmall
        onClick={saveFieldMappings}
        variant="bold"
        variantColor="blue"
      >
        Save mappings
      </CKButton>
    );
  };

  return (
    <div className="divide-y divide-gray-200">
      <Prompt
        message={'You have unsaved changes, are you sure you want to leave?'}
        when={mappingsAreDirty}
      />

      <FormSection
        className="mb-9"
        description={
          <p>
            Map Clearbit person and company attributes to {configurationName}{' '}
            form fields.
          </p>
        }
        title={`${configurationName} form fields`}
      >
        {loading || !fieldMappingData || !attributesData ? (
          <CKBox>
            <div className="flex items-center justify-center w-full h-full p-4">
              <CircularProgress />
            </div>
          </CKBox>
        ) : (
          <CKBox>
            <div className="px-5 py-2 font-medium text-gray-700 border-b border-gray-200 forms-mapping-row-grid">
              <p className="text-sm">Clearbit attributes</p>
              &nbsp;
              <p className="text-sm">{configurationName} form fields</p>
            </div>
            <div className="px-5">
              <div className="my-4 text-gray-600 space-y-4">
                {fieldMappings.map((mapping, index) => {
                  const rowKey = `${mapping.targetAttribute ||
                    'empty'}:${mapping.sourceAttribute || 'empty'}-${index}`;

                  return (
                    <FormFieldMappingRow
                      index={index}
                      key={rowKey}
                      mapping={mapping}
                      onChange={handleMappingChange(index)}
                      onClickRefreshAttributes={onClickRefreshAttributes}
                      onRemove={removeMapping(index)}
                      service={configurationName}
                      showError={showInvalidFields}
                      sourceAttributes={attributesData.sourceAttributes}
                      sourceErrors={fieldErrors[mapping.sourceAttribute]}
                      targetAttributes={attributesData.targetAttributes}
                      targetErrors={fieldErrors[mapping.targetAttribute]}
                      type={integration}
                    />
                  );
                })}
              </div>
            </div>
            <div className="flex content-center justify-between px-5 py-3 border-t border-gray-200 rounded-b bg-gray-50">
              <CKButton
                className="mr-4"
                isSmall
                leftIcon={<AddHollow />}
                onClick={addMapping}
                variantColor="blue"
              >
                Add mapping
              </CKButton>
              {areFieldMappingsInvalid ? (
                <CKTooltip tooltip="Some field mappings are invalid">
                  <div
                    onMouseEnter={() => setShowInvalidFields(true)}
                    onMouseLeave={() => setShowInvalidFields(false)}
                  >
                    <SaveButton />
                  </div>
                </CKTooltip>
              ) : (
                <SaveButton />
              )}
            </div>
          </CKBox>
        )}
      </FormSection>
    </div>
  );
}
